import { CommonModule, LocationStrategy } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectorRef, Component, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonContent, IonicModule, ModalController, NavParams, Platform } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject, Subscription, of } from "rxjs";
import { map, switchMap, takeUntil, withLatestFrom } from "rxjs/operators";

// Stores
import { Store } from "@ngrx/store";
import { NewTicketFacade, ticketState } from "@sports-features/ticket/stores";
import { SportsbookState } from "@sports-stores/sportsbook/index";
import { SportsbookFacade } from "@sports-stores/sportsbook/sportsbook.facade";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";

//Services
import { EventsService } from "@providers/EventsService";
import { ScriptService } from "@providers/ScriptService";
import { StyleService } from "@providers/StyleService";
import { BufferFacade } from "@providers/facades/buffer.facade";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { TicketService } from "@sports-services/index";
import { SportService } from "@sports-services/index";
import { SportNavigationService } from "@sports-services/index";

// Pipes
import { SportsExtractDatePipe } from "@sports-pipes/sports.extract-date.pipe";
import { SportsExtractHourPipe } from "@sports-pipes/sports.extract-hour.pipe";
import { SportsFormatOddPipe } from "@sports-pipes/sports.format-odd.pipe";

// Components
import { CodereFooterComponent } from "@components/mso/footer/codere-footer/codere-footer";
import {
  SbGridContentThumbnailComponent,
  SbGridFilterComponent,
  SbGridHeaderComponent,
  SbGridItemComponent,
  SbPrematchItemComponent,
  SbSimpleNavbarComponent,
  SbTabsComponent,
  SportsGridComponent,
} from "@sports-components/index";

// models
import { GlobalVarsModel, LicenseTypes } from "@models/index";
import { BetTypeEnum, CombinationMulticastInput, RaceLineItem, StraightMulticastInput } from "@ticket/models";

//Others
import { Login } from "@shared-pages/mso/login/login";
import { C_SportsRace, I_SportsHorses, PartialBet, raceMarketDataModel } from "@sports-models/index";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";
import { Utils } from "@sports-utils/Utils";
import { VIDEOPLAYER_ACTIONS } from "@sports-utils/index";

import { TicketItemTypesEnum } from "@sports-pages/ticket/utils/ticket.constants";
import {
  createCombinationMulticastResult,
  createLineItemFromRace,
  createStraightMulticastResult,
} from "@sports-pages/ticket/utils/ticket.helpers";

@Component({
  selector: "page-race-market",
  templateUrl: "./race-market.page.html",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ["./race-market.page.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    SbSimpleNavbarComponent,
    SbGridFilterComponent,
    SbPrematchItemComponent,
    SportsExtractHourPipe,
    SbGridHeaderComponent,
    SbGridItemComponent,
    SbGridContentThumbnailComponent,
    SportsFormatOddPipe,
    TranslateModule,
    CodereFooterComponent,
    SbTabsComponent,
    SportsGridComponent,
  ],
})
export class HorsesMarketPage extends SportsBasePage {
  @ViewChild(IonContent) content: IonContent;
  race$: BehaviorSubject<I_SportsHorses>;
  params: I_SportsHorses;
  _race: C_SportsRace;
  filters;
  currentFilter$ = new BehaviorSubject(null);
  participants$: Observable<any>;
  _participants$ = new Subject();
  replay = true;
  bet$: Subscription;
  bets$: Subscription;
  partialBet: PartialBet = new PartialBet();
  canBet: boolean;
  showingVideo = false;
  raceBets: string;
  bets;
  colEnabled;
  streamingSource;
  idInterval;
  raceHasOdds: boolean;
  events$: Observable<any>;
  eventsCollapsed = true;
  dropListEvents$: Observable<any>;
  dropListEventsBlocked$: Observable<any>;
  polling$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  header$: Observable<string[]>;

  showingXBNETVideo = true;
  videoActivated = false;
  isDesktop: boolean = false;
  isMobile: boolean = false;
  isHorseRaceUSA: boolean;
  _showError = false;
  globalVars: GlobalVarsModel;
  LicenseTypes = LicenseTypes;
  platform = inject(Platform);
  sportService = inject(SportService);
  ticketService = inject(TicketService);
  styleService = inject(StyleService);
  scriptService = inject(ScriptService);
  _translate = inject(TranslateService);

  newTicketFacade = inject(NewTicketFacade);
  store = inject(Store<ticketState>);
  sportsbookStore = inject(Store<SportsbookState>);
  bufferFacade = inject(BufferFacade);
  utils = inject(Utils);
  modalCtrl = inject(ModalController);
  extractDate = inject(SportsExtractDatePipe);
  deviceService = inject(DeviceService);
  newBaseService = inject(NewBaseService);
  eventService = inject(EventsService);
  navParams = inject(NavParams);
  locationStrategy = inject(LocationStrategy);
  sportNavigationService = inject(SportNavigationService);

  raceMarketData$: Observable<any>;
  raceMarketDataSub$: Subscription;
  sportbookFacade = inject(SportsbookFacade);

  router = inject(Router);

  firstIterationRestore = true;
  sportsNavigationId = undefined;
  stateToSave = {
    raceSelected: undefined,
    filter: undefined,
    scrollPosition: undefined,
  };
  stateToRestore = undefined;

  private readonly unsubscribe$ = new Subject();

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
    this.sportsNavigationId = currentUrl.state.sportsNavigationId;
    this.params = currentUrl.state.params;
    this.sportbookFacade.initRaceMarket(this.params);
    this.initializePage(this.params);
    this.race$ = new BehaviorSubject(undefined);

    this.eventService.subscribe(VIDEOPLAYER_ACTIONS.TO_TICKET, () => {
      if (this.globalVars.videoPlayerWindow) {
        this.showingVideo = false;
      }
    });
    this.eventService.subscribe(VIDEOPLAYER_ACTIONS.CLOSE, () => {
      this.showingVideo = false;
      this.content.scrollToTop();
    });

    this.raceMarketData$ = this.sportsbookStore.select(sportsbookSelector.getRaceMarketDataState);
    this.raceMarketDataSub$ = this.raceMarketData$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((raceMarketData: raceMarketDataModel) => {
        if (raceMarketData?.dropListEventSelected) {
          this.race$.next(raceMarketData?.dropListEventSelected);
        }
        this.filters = raceMarketData?.filters;
        if (!this.currentFilter$.getValue()) {
          this.filter(raceMarketData?.selectedFilter);
        }
        this.dropListEvents$ = of(raceMarketData?.dropListEvents);
        this.dropListEventsBlocked$ = of(!raceMarketData?.dropListEvents?.length);
        if (raceMarketData?.participants && raceMarketData?.participants !== "initialState") {
          this.participants$ = this.raceEvents(of(raceMarketData?.participants));
          this.setHeadersAndBets();
        }

        const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
        if (currentUrl?.popstate && this.firstIterationRestore && raceMarketData?.selectedFilter) {
          this.firstIterationRestore = false;
          this.sportsNavigationId = currentUrl.state.sportsNavigationId;
          this.stateToRestore = this.sportNavigationService.getRestoreStateFromHistory(this.sportsNavigationId);
          if (this.stateToRestore) {
            this.stateToSave = this.stateToRestore;
          }
          if (this.stateToRestore?.raceSelected) {
            this.navigate(this.stateToRestore.raceSelected);
            if (this.stateToRestore.filter) {
              setTimeout(() => {
                this.filter(this.stateToRestore?.filter);
              }, 300);
            }
            this.content.scrollToPoint(undefined, this.stateToRestore.scrollPosition || 0, 150);
          }
        }
      });
  }

  initializePage(params) {
    this.utils.hideFormAccessoryBar(false);
    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    this.trackingService.trackEvent([
      "SelectSport",
      params.NodeId,
      params.SportHandle,
      params.SportHandle,
      "pageView",
    ]);
  }

  setHeadersAndBets() {
    this.header$ = this.currentFilter$.pipe(
      map((current) => {
        if (!current) {
          return [];
        }
        switch (current.navigate) {
          case "win": {
            const h = ["CF", "GC"];
            if (this.raceHasOdds) {
              h.unshift("P");
            }
            return h;
          }
          case "forecast":
            return ["1º", "2º", "Orden"];
          case "tricast":
            return ["1º", "2º", "3º", "Orden"];
        }
      }),
    );

    this.bets$ = this.currentFilter$
      .pipe(
        withLatestFrom(this._participants$),
        map(([current, jockeys]) => {
          this.updateBetButtons(current.navigate, jockeys);
          this.setButtons(current.navigate);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: encodeURI(`/${this.params.SportHandle}/${this.params.Name}`),
      description: `Horses market page - ${this.params.Name}`,
      additionalData: {
        nodeId: this.params.NodeId,
        nodeName: this.params.Name,
        sportHandle: this.params.SportHandle,
      },
    };
  }

  raceEvents(raceElement): Observable<any> {
    return raceElement.pipe(
      map((race: C_SportsRace) => {
        this._race = race;
        this.race$.next({
          ...this.race$.getValue(),
          StartDate: race.StartDate,
        });
        this.filters = this.filters.map((filter, index) => {
          if (index === 2) {
            return {
              ...filter,
              disabled: this._race?.TricastDisabled,
            };
          }
          return filter;
        });

        this.isHorseRaceUSA = false;
        this.raceHasOdds = race?.Participants && race?.Participants[0]?.Odd !== -1;
        this._participants$.next(race?.Participants);
        !!race?.StreamingEnabled && race?.StreamingEnabled !== ""
          ? (this.videoActivated = true)
          : (this.videoActivated = false);
        this.changeDetectorRef.detectChanges();

        if (this._race.NodeId !== race.NodeId) {
          this.filter(this.filters[0]);
        }
        this.setButtons(this.currentFilter$.getValue().navigate);

        return race.Participants;
      }),
    );
  }

  setButtons(current) {
    switch (current) {
      case "win":
        //(participant.Odd === -1) || participant.IsNonRunner
        this.raceBets = !this.raceHasOdds ? "has-two-buttons" : "has-three-buttons";
        break;
      case "forecast":
        this.raceBets = "has-three-buttons";
        break;
      case "tricast":
        this.raceBets = "has-four-buttons";
        break;
    }
  }

  updateBetButtons(game: string, jockeys) {
    let numCol: number = 0;
    switch (game) {
      case "forecast":
        numCol = 3;
        break;
      case "tricast":
        numCol = 4;
        break;
    }

    this.colEnabled = [];
    this.partialBet.arrBet = [];
    this.bets = [];

    for (let j = 0; j < numCol; j++) {
      this.colEnabled.push(true);
      this.bets.push([]);
      for (let i = 0; i < jockeys.length; i++) {
        //
        this.bets[j].push(false);
      }
    }
    this.canBet = false;
    this.globalVars.hideBetFooterBar = this.canBet;

    const pollingActive = !this.bets.some((col) => col.some((v) => v));
    this.polling$.next(pollingActive);
  }

  filter(filter) {
    this.currentFilter$.next(filter);
    this.content?.scrollToTop(150);
    this.updateLastStateToRestore(this.race$.getValue(), filter, undefined);
  }

  blockColumn(num: number) {
    switch (this.currentFilter$.getValue().navigate) {
      case "forecast":
        if (num === 2) {
          //order
          this.colEnabled[0] = false;
          this.colEnabled[1] = false;
        } else {
          this.colEnabled[num] = false;
          this.colEnabled[2] = false;
        }
        break;
      case "tricast":
        if (num === 3) {
          //order
          this.colEnabled[0] = false;
          this.colEnabled[1] = false;
          this.colEnabled[2] = false;
        } else {
          this.colEnabled[num] = false;
          this.colEnabled[3] = false;
        }
        break;
    }
  }

  freeColumn(num: number) {
    switch (this.currentFilter$.getValue().navigate) {
      case "forecast":
      case "tricast":
        this.colEnabled[num] = true;

        if (this.partialBet.arrBet.length <= 0) {
          //no selection
          for (let i = 0; i < this.colEnabled.length; i++) {
            this.colEnabled[i] = true;
          }
        }

        break;
    }
  }

  toggle(race: I_SportsHorses) {
    const newRace: I_SportsHorses = {
      ...race,
      Selected: !race.Selected,
    };
    this.race$.next(newRace);
  }

  async switchVideo() {
    if (!this.globalVars.user.logged) {
      const modal = await this.modalCtrl.create({
        component: Login,
      });
      modal.present();
      modal.onDidDismiss().then(() => {
        if (this.globalVars.user.logged) {
          this.switchVideo();
        }
      });
      return;
    }

    if (this.globalVars.user.logged) {
      if (this.showingVideo) {
        this.showingVideo = false;
        this.eventService.publish(VIDEOPLAYER_ACTIONS.CLOSE);
        this.trackingService.trackEvent([
          "DeactiveStreamingsis",
          this.race$.getValue().NodeId,
          this.race$.getValue().SportHandle,
          "Streaming desactivado",
          "event",
        ]);
        this.content.scrollToTop();
      } else {
        Promise.all([
          this.scriptService.load("betradarliveplayer"),
          this.styleService.load("betradarliveplayer"),
        ]).then(() => {
          this.showingVideo = true;
          this.eventService.publish(VIDEOPLAYER_ACTIONS.RUN, {
            event: this.race$.getValue(),
          });

          this.content.scrollToTop();
        });
      }
    }
  }

  navigate(race: any) {
    this.sportbookFacade.setRaceMarketSelectedRace(race);
    this.filter(this.filters[0]);
    this.content.scrollToTop();
    this.updateLastStateToRestore(race, this.filters[0], undefined);
  }

  /*BETS*/

  /**
   * Adds a Multicast Selection
   * @param item LineItemSelected
   * @param col Column
   * @param index Index
   */
  addMulticastSelection(event, item, col: number, index: number): void {
    console.log({ event });

    const isSelected: boolean = this.bets[col][index];

    let numBets: number;
    let lastCol: boolean;
    let betType: string;
    switch (this.currentFilter$.getValue().navigate) {
      case "forecast":
        numBets = 2;
        betType = "Forecast";
        lastCol = col === 2;
        break;
      case "tricast":
        numBets = 3;

        betType = "Tricast";
        lastCol = col === 3;
        break;
    }

    if (isSelected) {
      this.bets[col][index] = false;

      const indexTemp = this.partialBet.arrBet.findIndex((myItem) => myItem.NodeId === item.NodeId);
      this.partialBet.arrBet.splice(indexTemp, 1);
      //
      this.freeColumn(col);
      //
    } else {
      this.bets[col][index] = true;
      this.blockColumn(col);

      //add item to bet. copy item to extend
      const itemTemp = <any>JSON.parse(JSON.stringify(item));

      let order: number;
      if (lastCol) {
        order = -1;
      } else {
        order = col + 1;
      }
      itemTemp.order = order;

      this.partialBet.ordered = !lastCol;
      this.partialBet.betType = betType;
      this.partialBet.arrBet.push(itemTemp);
    }

    if (this.partialBet.arrBet.length >= numBets) {
      //bet is complete
      if (!lastCol) {
        for (let i = 0; i < this.colEnabled.length; i++) {
          this.colEnabled[i] = false;
        }
      }
      //show process or clean bet
      this.canBet = true;
      this.globalVars.hideBetFooterBar = this.canBet;
    } else {
      this.canBet = false;
      this.globalVars.hideBetFooterBar = this.canBet;
    }

    const pollingActive = !this.bets.some((colElement) => colElement.some((v) => v));
    this.polling$.next(pollingActive);
  }

  /**
   * Place a SingleResult Bet on Betslip
   * @send singleResult Bet
   */
  betResult(event, participant, place: number, openPrice: boolean) {
    console.log({ event });

    const data = {
      SportId: this._race.SportId,
      SportHandle: this._race.SportHandle,
      LocationId: this._race.LocationId,
      LeagueId: this._race.PaddockId,
      EventId: this._race.NodeId,
      EventHasHandicap: this._race.EventHasHandicap,
      GameId: this._race.GameId,
      GameTypeId: this._race.GameTypeId,
      GameSpecialOddsValue: this._race.GameSpecialOddsValue,
      GameBetTypeAvailability: this._race.GameBetTypeAvailability,
      NumberOfStarters: this._race.Participants.length,
      Odd: place === 1 && openPrice ? -1 : participant.Odd,
      ResultId: participant.NodeId,
      BetType: BetTypeEnum.WINNER,
      CanBeEachWay: place === 2,
      NodeId: participant.NodeId,
      Name: participant.Name,
      Place: place,
      EventDate: this.extractDate.transform(this.race$.getValue().StartDate, [5]),
      EventName: this._race.Name + " " + this.extractDate.transform(this.race$.getValue().StartDate, [5]),
      GameName: this._race.Name,
    };

    this.newTicketFacade.checkSelection(data, participant.NodeId, TicketItemTypesEnum.LINE_ITEM);
  }

  /**
   * Place a Forecast or Tricast Bet on Betslip
   * @send straightMulticast (Forecast/Tricast) Bet
   */
  betMulticast() {
    let arrResults = [];
    const orderedResultIds: number[] = [];

    const sortedArrBet = this.partialBet.arrBet.sort((a, b) => a.order - b.order);

    arrResults = sortedArrBet.map((item) => {
      const newItem = {
        ResultId: item.NodeId,
        Places: [item.order],
      };
      return newItem;
    });

    arrResults.map((i) => orderedResultIds.push(+i.ResultId));

    const data = {
      Sport: {
        Id: this._race.SportId,
        SportHandle: this._race.SportHandle,
      },
      Location: {
        Id: this._race.LocationId,
      },
      League: {
        Id: this._race.PaddockId,
      },
      Event: {
        Id: this._race.NodeId,
        HasHandicap: this._race.EventHasHandicap,
      },
      Game: {
        Id: this._race.GameId,
        GameTypeId: this._race.GameTypeId,
        SpecialOddsValue: this._race.GameSpecialOddsValue,
        BetTypeAvailability: this._race.GameBetTypeAvailability,
        NumberOfStarters: this._race.Participants.length,
      },
      Magnitude: this.partialBet.betType === "Forecast" ? 2 : 3,
    };

    const partialBet: RaceLineItem = createLineItemFromRace(this._race, this.partialBet);

    if (this.partialBet.ordered) {
      const betStraightData: StraightMulticastInput = createStraightMulticastResult(data, arrResults);
      this.newTicketFacade.addStraighMulticastItem(betStraightData, partialBet);
    } else {
      const betCombinationData: CombinationMulticastInput = createCombinationMulticastResult(
        data,
        orderedResultIds,
      );
      this.newTicketFacade.addCombinationMulticastItem(betCombinationData, partialBet);
    }
    this.resetBet();
  }

  resetBet() {
    this.currentFilter$.next(this.currentFilter$.getValue());
  }

  ngOnDestroy() {
    this.utils.hideFormAccessoryBar(true);
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
    this.replay = false;
    this.sportNavigationService.injectLastStateToHistory(this.sportsNavigationId, this.stateToSave);
  }

  onScroll(ev) {
    this.updateLastStateToRestore(undefined, undefined, ev.detail.scrollTop);
  }

  updateLastStateToRestore(raceSelected, filter, scrollPosition) {
    const newStateToSave = { ...this.stateToSave };
    if (raceSelected !== undefined) {
      newStateToSave.raceSelected = raceSelected;
    }
    if (filter !== undefined) {
      newStateToSave.filter = filter;
    }
    if (scrollPosition !== undefined) {
      newStateToSave.scrollPosition = scrollPosition;
    }
    this.stateToSave = newStateToSave;
  }
}
